import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Input,
    Stack,
    Textarea,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import { AddIcon, EditIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { FileAttachmentMultipleInput } from "components/FileAttachmentInput/FileAttachmentMultipleInput";
import Loading from "components/Loading/Loading";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { ROUTES } from "constants/routes-path";
import { documentsService } from "services/documentsService";
import { documentsTypeService } from "services/documentsTypeService";
import { checkErrors, hasError } from "utils/forms-utils";
import DocumentsTypeModal from "./DocumentsType/DocumentsTypesModal";
import { parseDateBrToISODateString, formatDateInputToDateTimeString } from "utils/utils";
import { downloadFile, printFile } from "./DocumentUtils";
import ListPageCardUserHeader from "../Users/ListPageCardUserHeader";
import { userService } from "../../../services/userService";
import moment from "moment";
import "moment/locale/pt-br";
import DateTime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { LOCALE_PT_BR, DATE_FORMAT, DATE_FORMAT_ISO } from "constants/date-time";

function DocumentsNew() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { id } = useParams();
    const showToast = useCustomToast();
    const attachmentRef = useRef(undefined);

    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const { parentId, parentType } = location.state || {};
    const [formData, setFormData] = useState(undefined);
    const [userData, setUserData] = useState(undefined);
    const [documentTypesData, setDocumentTypesData] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [fileAttachmentName, setAttachmentFileName] = useState(null);
    const [inputError, setInputError] = useState({});
    const { isOpen: isOpenType, onOpen: onOpenType, onClose: onCloseType } = useDisclosure();

    const ids = useMemo(() => ({
        documentType: "type",
        documentDate: "documentData",
        documentExpirationDate: "expirationDate",
        documentObservations: "observations",
        documentFiles: "files",
        documentFileInfo: "fileInfo",
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

    const initFormData = (data) => ({
        [ids.documentType]: {
            value: data?.tipo_documento || "",
            validations: [{ required: true }],
        },
        [ids.documentDate]: {
            value: parseDateBrToISODateString(data?.data_documento) || new Date().toISOString().split("T")[0],
            validations: [{ required: true }],
        },
        [ids.documentExpirationDate]: {
            value: parseDateBrToISODateString(data?.data_validade),
            validations: [],
        },
        [ids.documentObservations]: {
            value: data?.observacoes || "",
            validations: [],
        },
        [ids.documentFiles]: {
            value: data?.anexos || [],
            validations: [],
        },
        [ids.documentFileInfo]: {
            value: data?.arquivo_info || [],
            validations: [],
        },
    });

    const fetchData = async () => {
        try {
            const documentTypes = await documentsTypeService.findMany();
            const typesList = documentTypes._embedded?.document_type_list ?? [];
            setDocumentTypesData(typesList);

            if (id) {
                const documentDetails = await documentsService.find(parentType, parentId, id);
                setAttachmentFileName(documentDetails.anexos || []);
                const selectedType = typesList.find(t => t.tipo.toUpperCase() === documentDetails.tipo_documento.toUpperCase());
                setSelectedType(selectedType);

                setFormData(initFormData({
                    ...documentDetails,
                    tipo_documento: selectedType?.tipo || documentDetails.tipo_documento,
                }));
            } else {
                setFormData(initFormData());

                if (parentType==="usuarios"){
                    const user = await userService.find(parentId);
                    setUserData(user);
                }
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const fetchDocumentsData = async () => {
        try {
            await documentsTypeService.findMany();
            if (id) {
                const documentDetails = await documentsService.find(parentType, parentId, id);
                setAttachmentFileName(documentDetails.anexos || []);
            }
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const fetchDocumentTypes = async () => {
        try {
            const res = await documentsTypeService.findMany();
            const documentTypes = res?._embedded?.document_type_list ?? [];
            setDocumentTypesData(documentTypes);
        } catch (error) {
            setDocumentTypesData([]);
        }
    };

    const handleSaveDocumentType = async (typeData) => {
        try {
            let updatedType;

            if (selectedType) {
                await documentsTypeService.update(selectedType.id, typeData);
                updatedType = { ...selectedType, ...typeData };
                showToast({ title: t("document-type.messages.success.edit"), status: "success" });
            } else {
                const createdType = await documentsTypeService.create(typeData);
                updatedType = createdType;
                showToast({ title: t("document-type.messages.success.create"), status: "success" });
            }

            await fetchDocumentTypes();
            await fetchData();

            setSelectedType(updatedType);
            setFormData((prevData) => ({
                ...prevData,
                [ids.documentType]: {
                    ...prevData[ids.documentType],
                    value: updatedType.tipo,
                },
            }));

            setInputError((prevError) => {
                const newErrors = { ...prevError };
                delete newErrors[ids.documentType];
                return newErrors;
            });

            onCloseType();
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const toFormData = () => {
        const formValues = {
            tipoDocumento: formData[ids.documentType].value,
            dataDocumento: formData[ids.documentDate].value,
            dataValidade: formData[ids.documentExpirationDate].value,
            observacoes: formData[ids.documentObservations].value,
        };

        const data = new FormData();
        id && data.append("parentID", parentId);
        data.append("parentType", parentType);
        id ? data.append("documentData", JSON.stringify(formValues)) : data.append("documentData", new Blob([JSON.stringify(formValues)], { type: "application/json" }));

        const files = attachmentRef?.current?.getFiles?.();
        if (files && Array.isArray(files)) {
            files.forEach((file) => {
                data.append("files", file);
            });
        }

        return data;
    };

    const hasUnreadableFiles = async () => {
        const files = attachmentRef?.current?.getFiles?.();

        for (const file of files || []) {
            try {
                await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve();
                    reader.onerror = () => reject(new Error(t("documents.messages.errors.file-unreadable")));
                    reader.readAsArrayBuffer(file);
                });
            } catch (err) {
                showToast({
                    title: t("documents.messages.errors.upload-failed") || err.message,
                    status: "error",
                });
                return true;
            }
        }

        return false;
    };

    const handleSubmit = async (e) => {
        if (isFormInvalid) return;
        if (await hasUnreadableFiles()) return;

        try {
            const data = toFormData();

            if (id) {
                await documentsService.updateDocument(data, id);
                showToast({ title: t("documents.messages.success.edit"), status: "success" });
            } else {
                await documentsService.createDocument(data, parentId);
                showToast({ title: t("documents.messages.success.create"), status: "success" });
            }

            navigate(`${ROUTES.documentsPath}?parent_id=${parentId}&parent_type=${parentType}`);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        const value = e.target.value;

        const newFormData = { ...formData, [id]: { ...formData[id], value } };
        setFormData(newFormData);
        setInputError(checkErrors({ formData: newFormData, inputError, name: id }));

        if (id === ids.documentType) {
            const selected = documentTypesData.find((type) =>
                type.tipo.toUpperCase() === value.trim().toUpperCase()
            );
            setSelectedType(selected || null);
        }
    };

    const handleDocumentDateChange = (value) => {
        const dateValue = moment(value).isValid()
            ? moment(value).format(DATE_FORMAT_ISO)
            : "";

        const newFormData = {
            ...formData,
            [ids.documentDate]: {
                ...formData[ids.documentDate],
                value: dateValue,
            },
        };

        setFormData(newFormData);
        setInputError((prev) =>
            checkErrors({
                formData: newFormData,
                inputError: prev,
                name: ids.documentDate,
            })
        );
    };

    const handleExpirationDateChange = (value) => {
        const dateValue = moment(value).isValid()
            ? moment(value).format(DATE_FORMAT_ISO)
            : "";

        const newFormData = {
            ...formData,
            [ids.documentExpirationDate]: {
                ...formData[ids.documentExpirationDate],
                value: dateValue,
            },
        };

        setFormData(newFormData);
        setInputError((prev) =>
            checkErrors({
                formData: newFormData,
                inputError: prev,
                name: ids.documentExpirationDate,
            })
        );
    };

    const handleDownloadFile = async (fileName) => {
        try {
            await downloadFile(parentType, parentId, id, fileName)
        } catch (error) {
            showToast({
                title: t("system.messages.errors.attachment-download"),
                status: "error",
            });
        }
    };

    const handlePrintFile = async (fileName) => {
        try {
            await printFile(parentType, parentId, id, fileName)
        } catch (error) {
            showToast({
                title: t("system.messages.errors.print-unavailable"),
                status: "error",
            });
        }
    };

    const handleDeleteFile = async (fileName) => {
        try {
            await documentsService.deleteAttachment(parentType, parentId, id, fileName);
            fetchDocumentsData();
        } catch (error) {
            showToast({
                title: t("system.messages.errors.attachment-delete"),
                status: "error",
            });
        }
    }

    useEffect(() => {
        fetchData();
    }, [id]);

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                {userData &&  (
                    <ListPageCardUserHeader userData={userData} />
                )}
                <ListPageCardHeader
                    title={t(id ? 'documents.form.edit-title' : 'documents.form.title')}
                />
                <CardBody px="22px">
                    {formData && documentTypesData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.documentType]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("documents.form.fields.type.label")}
                                    </FormLabel>
                                    <HStack align="center" justify="space-between">
                                        <SearchSelect
                                            required
                                            id="type"
                                            value={formData.type.value}
                                            items={documentTypesData}
                                            placeholder={t('documents.form.fields.type.placeholder')}
                                            displayField="tipo"
                                            valueField="tipo"
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.documentType])}
                                        </FormErrorMessage>
                                        <Button
                                            size="sm"
                                            colorScheme="blue"
                                            onClick={onOpenType}
                                        >
                                            {selectedType ? <EditIcon /> : <AddIcon />}
                                        </Button>
                                    </HStack>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={inputError[ids.documentDate]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.document-date.label")}
                                    </FormLabel>
                                    <DateTime
                                        dateFormat={DATE_FORMAT}
                                        timeFormat={false}
                                        locale={LOCALE_PT_BR}
                                        value={
                                            formData[ids.documentDate].value
                                                ? moment(formData[ids.documentDate].value)
                                                : ""
                                        }
                                        renderInput={(props) => (
                                            <Input
                                                {...props}
                                                variant="main"
                                                fontSize="sm"
                                                placeholder={t("documents.form.fields.document-date.placeholder")}
                                            />
                                        )}
                                        onChange={handleDocumentDateChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentDate])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError[ids.documentExpirationDate]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.expiration-date.label")}
                                    </FormLabel>
                                    <DateTime
                                        dateFormat={DATE_FORMAT}
                                        timeFormat={false}
                                        locale={LOCALE_PT_BR}
                                        value={
                                            formData[ids.documentExpirationDate].value
                                                ? moment(formData[ids.documentExpirationDate].value)
                                                : ""
                                        }
                                        renderInput={(props) => (
                                            <Input
                                                {...props}
                                                variant="main"
                                                fontSize="sm"
                                                placeholder={t("documents.form.fields.expiration-date.placeholder")}
                                            />
                                        )}
                                        onChange={handleExpirationDateChange}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentExpirationDate])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.documentObservations]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("documents.form.fields.observations.label")}
                                    </FormLabel>
                                    <Textarea
                                        minH="120px"
                                        bg={bgTextarea}
                                        id={ids.documentObservations}
                                        value={formData[ids.documentObservations].value}
                                        onChange={handleInputChange}
                                        _placeholder={{ color: placeholderColor }}
                                        placeholder={t('documents.form.fields.observations.placeholder')}
                                        fontSize="sm"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError[ids.documentObservations])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            <FileAttachmentMultipleInput onDownloadFile={handleDownloadFile}
                                                         onPrintFile={handlePrintFile}
                                                         onDeleteFile={handleDeleteFile}
                                                         ref={attachmentRef}
                                                         nameVariable={fileAttachmentName}
                                                         acceptedTypes={['application/pdf', 'image/jpeg']} />

                            <Flex w="100%" justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(`${ROUTES.documentsPath}?parent_id=${parentId}&parent_type=${parentType}`)}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="100px"
                                    variant="dark"
                                    fontSize="sm"
                                    onClick={handleSubmit}
                                    _hover={{
                                        bg: "grey.500",
                                        color: "white"
                                    }}
                                    isDisabled={isFormInvalid}
                                >
                                    {t(id ? 'system.actions.edit' : 'system.actions.save')}
                                </Button>
                            </Flex>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
            <DocumentsTypeModal
                isOpen={isOpenType}
                onClose={onCloseType}
                selectedType={selectedType}
                documentTypes={documentTypesData}
                onSave={handleSaveDocumentType}
            />
        </Flex >
    );
};

export default DocumentsNew;
